/**
 * Display formatting.
 *
 * The one rule that governs this file: **`null` renders as an em-dash, never as
 * zero.** A quote-only price displayed as "₦0" is not a formatting bug, it is a
 * factual claim the data does not support.
 */

const CURRENCY_SYMBOLS: Record<string, string> = {
  NGN: '₦', USD: '$', GBP: '£', EUR: '€',
  GHS: '₵', KES: 'KSh', ZAR: 'R', AUD: 'A$',
}

/** The universal "we do not know" glyph. */
export const UNKNOWN = '—'

/**
 * Format a decimal-string amount.
 *
 * Takes a string because the API sends one — see `types/api.ts`. Parsing to a
 * number is safe here because the result is thrown away after rendering; what
 * must never happen is a parsed float being written back.
 */
export function formatMoney(
  amount: string | number | null | undefined,
  currency = 'NGN',
  options: { compact?: boolean; decimals?: boolean } = {},
): string {
  if (amount === null || amount === undefined || amount === '') return UNKNOWN

  const value = typeof amount === 'string' ? Number.parseFloat(amount) : amount
  if (!Number.isFinite(value)) return UNKNOWN

  const symbol = CURRENCY_SYMBOLS[currency] ?? `${currency} `

  // Compact form for headline figures, where "₦1.2m" reads better than
  // "₦1,200,000.00" — but never in a comparison table, where the exact number
  // is the point.
  if (options.compact && Math.abs(value) >= 1_000_000) {
    return `${symbol}${(value / 1_000_000).toFixed(value >= 10_000_000 ? 0 : 1)}m`
  }
  if (options.compact && Math.abs(value) >= 10_000) {
    return `${symbol}${Math.round(value / 1000)}k`
  }

  const decimals = options.decimals === false ? 0 : value % 1 === 0 ? 0 : 2
  return `${symbol}${value.toLocaleString('en-NG', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`
}

/** A price range, or a single amount if both ends match. */
export function formatRange(
  min: string | null,
  max: string | null,
  currency = 'NGN',
): string {
  if (!min && !max) return UNKNOWN
  if (min && max && min !== max) {
    return `${formatMoney(min, currency)} – ${formatMoney(max, currency)}`
  }
  return formatMoney(min ?? max, currency)
}

export function formatNumber(value: number | null | undefined, compact = false): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return UNKNOWN
  if (compact && value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}m`
  if (compact && value >= 10_000) return `${Math.round(value / 1000)}k`
  return value.toLocaleString('en-NG')
}

export function formatPercent(
  value: string | number | null | undefined,
  options: { signed?: boolean } = {},
): string {
  if (value === null || value === undefined || value === '') return UNKNOWN
  const parsed = typeof value === 'string' ? Number.parseFloat(value) : value
  if (!Number.isFinite(parsed)) return UNKNOWN
  const sign = options.signed && parsed > 0 ? '+' : ''
  return `${sign}${parsed.toFixed(1)}%`
}

/* -- Dates ------------------------------------------------------------------
 *
 * Freshness is a first-class signal in this product: a price observed today and
 * one observed in March are different kinds of claim, and the interface has to
 * say so at a glance.
 */
export function formatDate(
  iso: string | null | undefined,
  options?: { short?: boolean },
): string {
  if (!iso) return UNKNOWN
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return UNKNOWN
  // `short` drops the year, for chart axis labels where the year is already
  // established by the range control and every tick repeating it is noise.
  return date.toLocaleDateString('en-NG', {
    day: 'numeric',
    month: 'short',
    ...(options?.short ? {} : { year: 'numeric' }),
  })
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return UNKNOWN
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return UNKNOWN
  return date.toLocaleString('en-NG', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return UNKNOWN
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return UNKNOWN

  const seconds = Math.floor((Date.now() - date.getTime()) / 1000)
  if (seconds < 45) return 'just now'
  if (seconds < 90) return 'a minute ago'

  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes} min ago`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`

  const days = Math.floor(hours / 24)
  if (days === 1) return 'yesterday'
  if (days < 30) return `${days} days ago`
  if (days < 365) return `${Math.floor(days / 30)} months ago`
  return `${Math.floor(days / 365)} years ago`
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || seconds < 0) return UNKNOWN
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`
  const hours = Math.floor(minutes / 60)
  return `${hours}h ${minutes % 60}m`
}

export function formatBytes(bytes: number | null | undefined): string {
  if (!bytes) return UNKNOWN
  const units = ['B', 'KB', 'MB', 'GB']
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`
}

/* -- Vocabulary labels ------------------------------------------------------
 *
 * The API sends machine values (`service_provider`); the interface shows
 * "Service Provider". Mapping here rather than server-side keeps translation a
 * client concern and the API stable.
 */
const LABEL_OVERRIDES: Record<string, string> = {
  ngo: 'NGO',
  fmcg: 'FMCG',
  lga: 'LGA',
  sme: 'SME',
  it: 'IT',
  in_stock: 'In stock',
  out_of_stock: 'Out of stock',
  on_request: 'On request',
  preorder: 'Pre-order',
  officially_verified: 'Officially verified',
  partially_verified: 'Partially verified',
  quote: 'Price on request',
  starting_from: 'From',
  super_admin: 'Super admin',
  business_owner: 'Business owner',
  structured_data: 'Structured data',
  user_submitted: 'User submitted',
  well_corroborated: 'Well corroborated',
  limited_corroboration: 'Limited corroboration',
  minimal_corroboration: 'Minimal corroboration',
}

export function humanise(value: string | null | undefined): string {
  if (!value) return UNKNOWN
  const override = LABEL_OVERRIDES[value]
  if (override) return override
  const words = value.replace(/[_-]+/g, ' ').trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

export function initials(name: string | null | undefined): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/** Strip the scheme so a source list reads as domains, not URLs. */
export function displayUrl(url: string | null | undefined, maxLength = 44): string {
  if (!url) return UNKNOWN
  const stripped = url.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '')
  return stripped.length > maxLength ? `${stripped.slice(0, maxLength - 1)}…` : stripped
}

/** Join non-empty parts — "Onitsha, Anambra" without a dangling comma. */
export function joinParts(...parts: Array<string | null | undefined>): string {
  const kept = parts.filter((part): part is string => Boolean(part && part.trim()))
  return kept.length ? kept.join(', ') : UNKNOWN
}

/* -- Freshness -------------------------------------------------------------- */
export type Freshness = 'fresh' | 'recent' | 'ageing' | 'stale' | 'unknown'

/**
 * How much weight a reader should give an observation's age.
 *
 * Nigerian prices move quickly, so the bands are deliberately tighter than a
 * generic directory would use: a six-week-old cement price is not current.
 */
export function freshnessOf(days: number | null | undefined): Freshness {
  if (days === null || days === undefined) return 'unknown'
  if (days <= 7) return 'fresh'
  if (days <= 30) return 'recent'
  if (days <= 90) return 'ageing'
  return 'stale'
}

export const FRESHNESS_LABEL: Record<Freshness, string> = {
  fresh: 'Fresh',
  recent: 'Recent',
  ageing: 'Ageing',
  stale: 'Stale',
  unknown: 'Never checked',
}
